import { ASSET_TYPES } from '@/shared/api/openmetadata';
import { getDefaultTabByAssetType } from '@/shared/lib/asset';
import { ensureFqnQuotes, parseFqnToPathParts } from '@/shared/lib/format';
import { getTagRoute } from '@/shared/lib/tags';

import { RUN_RELATED_TYPES } from '@/entities/asset-search';

/**
 * Asset 타입과 FQN으로 상세 페이지 URL을 생성합니다.
 *
 * @description
 * FQN 앞뒤의 불필요한 큰따옴표만 제거하고, 내부 특수문자를 감싼 따옴표는 유지합니다.
 *
 * @param type - Asset 타입 (예: 'table', 'glossaryTerm', 'tag', 'service' 등)
 * @param fqn - Asset의 Fully Qualified Name
 * @param serviceType - Service 타입 (service인 경우 필수)
 * @param id - Asset ID (실험 자산의 경우 실제 asset ID)
 * @param projectId - 프로젝트 ID (실험 자산의 경우 필수)
 * @returns Asset 상세 페이지 URL
 *
 * @example
 * buildAssetDetailUrl('table', 'mydb.schema.table') // -> '/table/mydb.schema.table/columns'
 * buildAssetDetailUrl('table', 'mydb.schema."system.sessions"') // -> '/table/mydb.schema."system.sessions"/columns'
 * buildAssetDetailUrl('table', '"mydb.schema.\\"system.sessions\\""') // -> '/table/mydb.schema."system.sessions"/columns'
 * buildAssetDetailUrl('glossaryTerm', 'manufacturing.term1') // -> '/glossary/manufacturing/term1/glossary_terms'
 * buildAssetDetailUrl('glossaryTerm', 'manufacturing.parent.term1') // -> '/glossary/manufacturing/parent.term1/glossary_terms'
 * buildAssetDetailUrl('tag', 'AAS_Aspect.Capabilities') // -> '/tags/detail/AAS_Aspect.Capabilities' -> '/tags/AAS_Aspect/Capabilities'
 * buildAssetDetailUrl('tag', 'AAS_Aspect') // -> '/tags/AAS_Aspect'
 * buildAssetDetailUrl('service', 'myservice', 'databaseService') // -> '/service/databaseService/myservice/insights'
 * buildAssetDetailUrl('run', 'exp-101', undefined, 'run-101-1', 'proj-001') // -> '/experiments/proj-001/exp-101/run-101-1?assetType=run'
 * buildAssetDetailUrl('artifact', 'exp-101', undefined, '150', 'proj-001') // -> '/experiments/proj-001/exp-101/104?assetType=artifact&assetId=150'
 * buildAssetDetailUrl('dataset', '/experiments/proj-001/exp-101/284/403?assetType=dataset') // 통과 그대로 (통합 검색 경로 등)
 */
export const buildAssetDetailUrl = (
  type: string,
  fqn: string,
  serviceType?: string,
  id?: string,
  projectId?: string,
  assetId?: string
): string => {
  if (fqn.startsWith('/experiments/')) {
    return fqn;
  }

  const cleanedFqn = ensureFqnQuotes(fqn);
  const defaultTab = getDefaultTabByAssetType(type);

  if (type === ASSET_TYPES.TAG) {
    return getTagRoute(cleanedFqn);
  }

  if (type === 'experiment') {
    const experimentId = /^\d+$/.test(cleanedFqn) ? cleanedFqn : id;
    if (experimentId && projectId) {
      return `/experiments/${projectId}/${experimentId}`;
    }
    console.warn(
      `buildAssetDetailUrl: Required parameters missing for experiment (projectId: ${projectId}, experimentId: ${experimentId}). Redirecting to root.`
    );
    return '/';
  }

  if (RUN_RELATED_TYPES.some((t) => t === type)) {
    const experimentId = cleanedFqn;
    const runId = id;
    if (experimentId && runId && projectId) {
      if (assetId && type !== 'run') {
        return `/experiments/${projectId}/${experimentId}/${runId}/${assetId}?assetType=${type}`;
      }
      return `/experiments/${projectId}/${experimentId}/${runId}?assetType=${type}`;
    }
    console.warn(
      `buildAssetDetailUrl: Required parameters missing for experiment asset (type: ${type}, projectId: ${projectId}, experimentId: ${experimentId}, runId: ${runId}). Redirecting to root.`
    );
    return '/';
  }

  if (type === ASSET_TYPES.GLOSSARY_TERM) {
    const fqnParts = parseFqnToPathParts(cleanedFqn, true);
    if (fqnParts.length > 1) {
      const termCategory = fqnParts[0];
      const termFqnSlice = fqnParts.slice(1);
      const termFqn = termFqnSlice.join('.');

      return `/glossary/${termCategory}/${termFqn}/${defaultTab}`;
    }
    return `/glossary/${cleanedFqn}`;
  }

  if (type === ASSET_TYPES.SERVICE && serviceType) {
    return `/service/${serviceType}/${cleanedFqn}/${defaultTab}`;
  }

  if (defaultTab) {
    return `/${type}/${cleanedFqn}/${defaultTab}`;
  }

  return `/${type}/${cleanedFqn}`;
};
